/**
 * ACCEPT A PHIBIAN CONSOLE STAFF INVITATION (dual onboarding, part A).
 *
 * Reached by the one-time link an administrator sent:
 * `/accept-invitation#token=pci_…`. The token is read from the URL FRAGMENT,
 * which browsers never send to a server, and the fragment is cleared from the
 * address bar as soon as it is read.
 *
 * 1. The invited email + a password: an existing Identity Platform account's,
 *    or a new one ("create my account").
 * 2. An authenticator: enroll one here if the account has none (the secret is
 *    shown once), or enter its code if it does. Text messages are not accepted.
 * 3. Access is granted. Sign in normally afterwards.
 *
 * Nothing here creates a customer account or touches a tenant.
 */
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import {
  GOOGLE_ERROR_MESSAGES,
  GOOGLE_START_PATH,
  acceptCall,
  googleIntent,
  readGoogleReturn,
  staffRequests,
} from '../platform/staffSession.js';
import type { AcceptStep } from '../platform/staffSession.js';

const GOOGLE_SIGN_IN = import.meta.env.VITE_STAFF_GOOGLE_SIGN_IN === 'on';

function readToken(): string {
  const match = /(?:^|[#&])token=([^&]+)/.exec(window.location.hash);
  return match ? decodeURIComponent(match[1]!) : '';
}

/**
 * The new authenticator, shown once: a QR code drawn here in the browser from
 * the otpauth URI (the setup key never goes to a QR service), and the key by
 * hand as the fallback. Nothing here is stored or logged.
 */
export function EnrollAuthenticator({
  otpauthUri,
  secret,
}: {
  otpauthUri: string;
  secret: string;
}) {
  return (
    <>
      <p>
        Scan this code with your authenticator app (Apple Passwords, Google Authenticator or
        similar). It adds a new <strong>Phibian Console</strong> entry and is shown only now.
      </p>
      <QRCodeSVG
        value={otpauthUri}
        size={200}
        level="M"
        marginSize={2}
        title="Phibian Console authenticator setup code"
      />
      <details>
        <summary>Can’t scan? Set it up by hand</summary>
        <p>
          <a href={otpauthUri}>Open in an authenticator app on this device</a>, or enter this setup
          key (time-based, 6 digits):
        </p>
        <p>
          <code style={{ wordBreak: 'break-all' }}>{secret}</code>
        </p>
      </details>
    </>
  );
}

export function AcceptInvitation({ apiBase }: { apiBase: string }) {
  const [token] = useState(() => {
    const t = readToken();
    if (t) window.history.replaceState(null, '', window.location.pathname);
    return t;
  });
  // Back from Google with a sealed result (or a refusal). Read once, cleared.
  const [googleBack] = useState(() => {
    const back = readGoogleReturn(window.location.hash);
    if (back) window.history.replaceState(null, '', window.location.pathname);
    return back;
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [createAccount, setCreateAccount] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<AcceptStep | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handle = (next: AcceptStep) => {
    setBusy(false);
    if (next.kind === 'refused' || next.kind === 'transport') {
      setProblem(next.message);
      // A wrong code keeps the current step; anything else starts over.
      if (next.kind === 'refused' && next.code === 'code_invalid') return;
      if (next.kind === 'refused') setStep(null);
      return;
    }
    setProblem(null);
    setPassword('');
    setCode('');
    setStep(next);
  };

  useEffect(() => {
    if (googleBack?.kind !== 'accept') return;
    setBusy(true);
    void acceptCall(apiBase, staffRequests.acceptGoogleContinue(googleBack.state)).then(handle);
  }, [googleBack]);

  const continueWithGoogle = () => {
    setBusy(true);
    void googleIntent(apiBase, token).then((intent) => {
      if (!intent) {
        setBusy(false);
        setProblem(GOOGLE_ERROR_MESSAGES.invitation_invalid!);
        return;
      }
      window.location.assign(`${GOOGLE_START_PATH}?intent=${encodeURIComponent(intent)}`);
    });
  };

  if (googleBack?.kind === 'error' && !step) {
    return (
      <section>
        <h2>Accept an invitation</h2>
        <div className="banner" role="alert">
          {GOOGLE_ERROR_MESSAGES[googleBack.reason] ?? GOOGLE_ERROR_MESSAGES.sign_in_failed}
        </div>
      </section>
    );
  }

  if (!token && googleBack?.kind !== 'accept') {
    return (
      <section>
        <h2>Accept an invitation</h2>
        <div className="banner" role="alert">
          This page needs the invitation link exactly as it was sent to you. Ask the person who
          invited you for a new one.
        </div>
      </section>
    );
  }

  if (step?.kind === 'not_enabled') {
    return (
      <section>
        <h2>Accept an invitation</h2>
        <p>Individual staff access is not switched on for this environment yet.</p>
      </section>
    );
  }

  if (step?.kind === 'accepted') {
    return (
      <section>
        <h2>You have Phibian Console access</h2>
        <p>
          Your access is active as <strong>{step.role.replace(/_/g, ' ')}</strong>.{' '}
          <a href="/">Sign in to the console</a> with Google or your email and password, then your
          authenticator.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h2>Accept your Phibian Console invitation</h2>
      {problem ? (
        <div className="banner" role="alert">
          {problem}
        </div>
      ) : null}

      {step?.kind === 'enroll' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            void acceptCall(
              apiBase,
              staffRequests.acceptEnroll(step.enrollment, code.trim(), displayName),
            ).then(handle);
          }}
        >
          <EnrollAuthenticator otpauthUri={step.otpauthUri} secret={step.secret} />
          <CodeField code={code} setCode={setCode} />
          <NameField displayName={displayName} setDisplayName={setDisplayName} />
          <p>
            <button type="submit" disabled={busy || code.length !== 6}>
              Confirm and accept
            </button>
          </p>
        </form>
      ) : step?.kind === 'verify' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            void acceptCall(
              apiBase,
              staffRequests.acceptVerify(step.challenge, code.trim(), displayName),
            ).then(handle);
          }}
        >
          <CodeField code={code} setCode={setCode} />
          <NameField displayName={displayName} setDisplayName={setDisplayName} />
          <p>
            <button type="submit" disabled={busy || code.length !== 6}>
              Verify and accept
            </button>
          </p>
        </form>
      ) : (
        <>
          {GOOGLE_SIGN_IN ? (
            <>
              <p>
                <button type="button" disabled={busy} onClick={continueWithGoogle}>
                  Continue with Google
                </button>
              </p>
              <p className="quiet">
                Use the Google account you were invited at. You then add an authenticator app (or
                confirm the one you have) before your access is active.
              </p>
              <h3>Or use an email and password</h3>
            </>
          ) : null}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setBusy(true);
              void acceptCall(
                apiBase,
                staffRequests.acceptStart(token, email.trim(), password, createAccount),
              ).then(handle);
            }}
          >
            <label htmlFor="acceptEmail">The email address you were invited at</label>
            <input
              id="acceptEmail"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label htmlFor="acceptPassword">
              {createAccount ? 'Choose a password' : 'Password'}
            </label>
            <input
              id="acceptPassword"
              type="password"
              autoComplete={createAccount ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <label>
              <input
                type="checkbox"
                checked={createAccount}
                onChange={(e) => setCreateAccount(e.target.checked)}
              />{' '}
              I don&apos;t have an account yet — create one
            </label>
            <p>
              <button type="submit" disabled={busy || !email || !password}>
                Continue
              </button>
            </p>
          </form>
        </>
      )}
    </section>
  );
}

function CodeField({ code, setCode }: { code: string; setCode: (v: string) => void }) {
  return (
    <>
      <label htmlFor="acceptCode">6-digit code from your authenticator app</label>
      <input
        id="acceptCode"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      />
    </>
  );
}

function NameField({
  displayName,
  setDisplayName,
}: {
  displayName: string;
  setDisplayName: (v: string) => void;
}) {
  return (
    <>
      <label htmlFor="acceptName">Your name (shown to other staff)</label>
      <input
        id="acceptName"
        autoComplete="name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
      />
    </>
  );
}
